import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, CheckCircle2, KeyRound, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ui } from "../lib/ui";

export default function GetAccess() {
  const { user, checked, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState("");
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    api.get("/access/status").then((r) => setConfigured(r.data.shopify_configured)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/access/verify-purchase", { order_number: order });
      await refreshMe();
      toast.success("Purchase verified. Full archive unlocked!");
      navigate("/archive");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!checked) return <div className="max-w-md mx-auto px-4 py-20 text-[#5A4441]">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-8 py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#F3E5D8] flex items-center justify-center mx-auto mb-5"><KeyRound size={24} className="text-[#8B1A1A]" /></div>
        <h1 className="font-serif text-3xl font-bold text-[#8B1A1A] mb-2">Get archive access</h1>
        <p className="text-sm text-[#5A4441] mb-6">Please log in or create an account first, then verify your Élite TEF Prep purchase.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/login" className={ui.btnPrimary} state={{ from: "/get-access" }}>Log in</Link>
          <Link to="/register" className={ui.btnSecondary}>Create account</Link>
        </div>
        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-[#E8DCC8]" />
          <span className={ui.caption}>No pack yet?</span>
          <div className="h-px flex-1 bg-[#E8DCC8]" />
        </div>
        <a
          href="https://elitetefprep.com"
          target="_blank"
          rel="noopener noreferrer"
          className={ui.btnSecondary + " w-full"}
          data-testid="buy-pack-btn-anon"
        >
          <ShoppingBag size={16} /> Buy a prep pack from Élite TEF Prep
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-16">
      <div className="w-14 h-14 rounded-2xl bg-[#F3E5D8] flex items-center justify-center mb-5"><ShoppingBag size={24} className="text-[#8B1A1A]" /></div>
      <h1 className="font-serif text-3xl font-bold text-[#8B1A1A] mb-2">Get archive access</h1>

      {user.access_level === "member" ? (
        <div className={ui.card + " p-6 flex items-start gap-3 mt-6"} data-testid="access-member-banner">
          <CheckCircle2 size={20} className="text-[#8B1A1A] mt-0.5" />
          <div>
            <p className="text-[#210201] font-medium">You already have full access.</p>
            <Link to="/archive" className="text-[#8B1A1A] underline text-sm">Browse the archive →</Link>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-[#5A4441] mb-6">
            Bought a prep pack from <a href="https://elitetefprep.com" target="_blank" rel="noopener noreferrer" className="text-[#8B1A1A] underline">Élite TEF Prep</a>?
            Enter your order number to unlock the full archive instantly. (Submitting an approved report also unlocks access.)
          </p>
          {!configured && (
            <div className={ui.card + " p-4 mb-5 bg-[#F3E5D8] text-sm text-[#671813]"}>
              Purchase verification isn't switched on yet. Please check back soon.
            </div>
          )}
          <form onSubmit={submit} className={ui.card + " p-6 space-y-5"} data-testid="get-access-form">
            <div>
              <label className={ui.label}>Shopify order number</label>
              <input value={order} onChange={(e) => setOrder(e.target.value)} placeholder="e.g. #1042" className={ui.input} required data-testid="order-number-input" />
            </div>
            <button type="submit" disabled={loading || !configured} className={ui.btnPrimary + " w-full"} data-testid="verify-purchase-btn">
              {loading ? "Verifying…" : "Verify & unlock"}
            </button>
          </form>

          {/* Purchase path for visitors who don't have a pack yet */}
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#E8DCC8]" />
            <span className={ui.caption}>No pack yet?</span>
            <div className="h-px flex-1 bg-[#E8DCC8]" />
          </div>
          <div className={ui.card + " p-6 bg-[#F5F0E8]"} data-testid="buy-pack-card">
            <div className="flex items-start gap-3">
              <Sparkles size={20} className="text-[#8B1A1A] mt-0.5 shrink-0" />
              <div className="flex-1">
                <h2 className="font-serif text-lg font-semibold text-[#8B1A1A]">Get a prep pack</h2>
                <p className="text-sm text-[#5A4441] mt-1 leading-relaxed">
                  Grab an Élite TEF Prep pack to study the prediction banks — then unlock the full report archive here with your order number.
                </p>
                <a
                  href="https://elitetefprep.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ui.btnPrimary + " mt-4"}
                  data-testid="buy-pack-btn"
                >
                  <ShoppingBag size={16} /> Buy a prep pack
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
